import {
  Button,
  FormControl,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TextField,
  Switch,
  Box,
  TableColumn
} from "flux";
import { useCallback, useEffect, useState } from "react";
import Page from "@adora/shared/components/layout/page";
import useExperiments, { Experiment } from "../../hooks/use-experiments";
import { useNavigate, useParams } from "react-router-dom";
import useBrandName from "../../hooks/use-brand-name";
import { CommandBarWrapper } from "@adora/shared/components/command-bar/command-bar-wrapper";
import useExperimentProducts from "../../hooks/use-experiment-products";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import ViewExperimentDetailsDialog from "./details-dialog";
import LaunchExperimentDialog from "../../components/launch-experiments-dialog";

const ExperimentsPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const brandName = useBrandName(brandId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const {
    experiments,
    isLoading,
    error,
    markExperimentEvaled,
    rerunExperimentEval,
    retryExperiment,
    updateExperiment
  } = useExperiments(Number(brandId));
  const navigate = useNavigate();

  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteText, setNoteText] = useState("");

  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>("desc");
  const [filteredExperiments, setFilteredExperiments] = useState<Experiment[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { products, isLoading: productsLoading } = useExperimentProducts(Number(brandId));
  const { openDialog } = useDialog();
  const [isSmallTitle, setIsSmallTitle] = useState(false);

  // This is not a ususal thing to do. don't do this.
  const MANUAL_BACKGROUND_COLOR = "#F2F2F3";

  useEffect(() => {
    if (!experiments) {
      setFilteredExperiments([]);
      return;
    }

    let filtered = [...experiments];

    // Apply product filter
    if (selectedProduct) {
      filtered = filtered.filter((exp) => exp.product === selectedProduct);
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((exp) => exp.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Apply sorting based on sort direction
    if (sortDirection === "desc" || sortDirection === "asc") {
      // Sort by hit rate
      filtered.sort((a, b) => {
        const aRate = a.filtered_hit_rate || 0;
        const bRate = b.filtered_hit_rate || 0;

        return sortDirection === "desc" ? bRate - aRate : aRate - bRate;
      });
    } else {
      // Sort by ID when sortDirection is null
      filtered.sort((a, b) => a.id - b.id);
    }

    setFilteredExperiments(filtered);
  }, [experiments, selectedProduct, sortDirection, searchQuery]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, experiment: Experiment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedExperiment(experiment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedExperiment(null);
  };

  const handleViewImages = () => {
    if (selectedExperiment) {
      navigate(`/brands/${brandId}/experiments/${selectedExperiment.id}/creatives`);
    }
    handleMenuClose();
  };

  const handleViewDetails = async () => {
    if (selectedExperiment) {
      handleMenuClose();
      await openDialog(ViewExperimentDetailsDialog, { experiment: selectedExperiment });
    }
  };

  const handleMarkEvaled = () => {
    if (selectedExperiment) {
      markExperimentEvaled(selectedExperiment.id);
    }
    handleMenuClose();
  };

  const handleRerunEval = () => {
    const confirmed = window.confirm(
      "Are you sure you want to rerun this eval? This will delete any existing eval images and results that have already been run for this experiment"
    );
    if (confirmed && selectedExperiment) {
      rerunExperimentEval(selectedExperiment.id);
    }
    handleMenuClose();
    navigate("/batches");
  };

  const handleRetryExperiment = () => {
    const confirmed = window.confirm(
      "Are you sure you want to retry this experiment? This will delete any existing eval images and old experiment details will be unavailable."
    );
    if (confirmed && selectedExperiment) {
      retryExperiment(selectedExperiment.id);
    }
    handleMenuClose();
    navigate("/batches");
  };

  // Convert experiment info to LoraTrainingData format

  const handleRetrainExperiment = async () => {
    if (selectedExperiment && selectedExperiment.brand_id) {
      handleMenuClose();
      const confirm = await openDialog(LaunchExperimentDialog, {
        brandId: selectedExperiment?.brand_id,
        experimentId: selectedExperiment?.id
      });
      if (confirm) {
        navigate("/batches");
      }
    }
  };

  const startEditingNote = useCallback((experiment: Experiment) => {
    setEditingNoteId(experiment.id);
    setNoteText(experiment.result_notes || "");
  }, []);

  const cancelEditingNote = useCallback(() => {
    setEditingNoteId(null);
    setNoteText("");
  }, []);

  const saveNote = useCallback(
    (experimentId: number) => {
      updateExperiment(experimentId, { result_notes: noteText });
      setEditingNoteId(null);
    },
    [noteText, updateExperiment]
  );

  const toggleSortDirection = () => {
    setSortDirection((prev) => {
      if (prev === "desc") return "asc";
      if (prev === "asc") return null;
      return "desc";
    });
  };

  const handleProductChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedProduct(event.target.value as string);
  };

  const columns = [
    { field: "id", value: "ID", width: 80 },
    { field: "name", value: "Name", width: 200 },
    {
      field: "result_notes",
      value: "Result Notes",
      // width:  editingNoteId === experiment.id ? 300 : 100,
      renderCell: (experiment: Experiment) => {
        const isEditing = editingNoteId === experiment.id;

        return isEditing ? (
          <Box
            sx={{
              width: 100,
              opacity: 0,
              animation: "expandWidth 0.2s ease-out, fadeIn 0.2s ease-in forwards",
              "@keyframes expandWidth": {
                from: {
                  height: "73px",
                  opacity: 0
                },
                to: {
                  height: "100px",
                  opacity: 1
                }
              },
              "@keyframes fadeIn": {
                from: {
                  opacity: 0
                },
                to: {
                  opacity: 1
                }
              }
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", width: "300px", gap: "8px" }}>
              <TextField
                value={noteText}
                onChange={(e) => setNoteText(e.target.value as string)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.shiftKey) {
                    return saveNote(experiment.id);
                  }

                  if (e.key === "Escape") {
                    return cancelEditingNote();
                  }
                }}
                size="small"
                placeholder="Add notes..."
                variant="outlined"
                multiline
                rows={2}
                fullWidth
                dataDogIdentifier="experiment notes input"
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => saveNote(experiment.id)}
                  dataDogIdentifier="experiment notes save"
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={cancelEditingNote}
                  dataDogIdentifier="experiment notes cancel"
                >
                  Cancel
                </Button>
              </div>
            </Box>
          </Box>
        ) : (
          <div style={{ display: "flex", alignItems: "center", width: !experiment?.result_notes ? "100px" : "300px" }}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                startEditingNote(experiment);
              }}
              dataDogIdentifier="experiment notes edit"
            >
              Edit notes
            </Button>
            <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ml: 2 }}>
              {experiment.result_notes || ""}
            </Box>
          </div>
        );
      }
    },
    { field: "product", value: "Product", width: 300 },
    {
      field: "filtered_hit_rate",
      value: "Filtered Hit Rate",
      width: 100,

      renderCell: (experiment: Experiment) =>
        experiment.filtered_hit_rate !== undefined && experiment.filtered_hit_rate != null
          ? `${(experiment.filtered_hit_rate * 100).toFixed(1)}%`
          : "-"
    },
    {
      field: "blob_rate",
      value: "Blob Rate",
      width: 100,
      renderCell: (experiment: Experiment) =>
        experiment.blob_rate !== undefined ? `${(experiment.blob_rate * 100).toFixed(1)}%` : "-"
    },
    {
      field: "wrong_shoe_rate",
      value: "Wrong Shoe Rate",
      // value: "Wrong Shoe Rate",
      width: 120,
      renderCell: (experiment: Experiment) =>
        experiment.wrong_shoe_rate !== undefined ? `${(experiment.wrong_shoe_rate * 100).toFixed(1)}%` : "-"
    },
    {
      field: "incoherent_rate",
      value: "Incoherent Rate",
      width: 120,
      renderCell: (experiment: Experiment) =>
        experiment.incoherent_rate !== undefined ? `${(experiment.incoherent_rate * 100).toFixed(1)}%` : "-"
    },
    {
      field: "auto_accepted_rate",
      value: "Auto Accept",
      width: 100,
      renderCell: (experiment: Experiment) =>
        experiment.auto_accepted_rate !== undefined ? `${(experiment.auto_accepted_rate * 100).toFixed(1)}%` : "-"
    },

    {
      field: "manual_blob_rate",
      value: "Manual Blob Rate",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_blob_rate !== undefined ? `${(experiment.manual_blob_rate * 100).toFixed(1)}%` : "-"
    },
    {
      field: "manual_wrong_shoe_rate",
      value: "Manual Wrong Shoe Rate",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_wrong_shoe_rate !== undefined
          ? `${(experiment.manual_wrong_shoe_rate * 100).toFixed(1)}%`
          : "-"
    },
    {
      field: "manual_incoherent_rate",
      value: "Manual Incoherent Rate",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_incoherent_rate !== undefined
          ? `${(experiment.manual_incoherent_rate * 100).toFixed(1)}%`
          : "-"
    },
    {
      field: "manual_bad_laces_rate",
      value: "Manual Bad Laces Rate",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_bad_laces_rate !== undefined ? `${(experiment.manual_bad_laces_rate * 100).toFixed(1)}%` : "-"
    },
    {
      field: "manual_bad_fine_details_rate",
      value: "Manual Bad Fine Details Rate",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_bad_fine_details_rate !== undefined
          ? `${(experiment.manual_bad_fine_details_rate * 100).toFixed(1)}%`
          : "-"
    },
    {
      field: "manual_accepted_rate",
      value: "Manual Accept",
      width: 100,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) =>
        experiment.manual_accepted_rate !== undefined && experiment.manual_accepted_rate != null
          ? `${(experiment.manual_accepted_rate * 100).toFixed(1)}%`
          : "-"
    },
    {
      field: "overfitting_manual",
      value: "Manual Overfitting",
      width: 150,
      backgroundColor: MANUAL_BACKGROUND_COLOR,
      renderCell: (experiment: Experiment) => (
        <Select
          value={experiment.overfitting_manual || ""}
          onChange={(e) => {
            const value = e.target.value as string;
            updateExperiment(experiment.id, { overfitting_manual: value });
          }}
          size="small"
          displayEmpty
        >
          <MenuItem value="">Select...</MenuItem>
          <MenuItem value="unacceptable">Unacceptable</MenuItem>
          <MenuItem value="moderate">Moderate</MenuItem>
          <MenuItem value="acceptable">Acceptable</MenuItem>
        </Select>
      )
    },
    { field: "description", value: "Description", width: 300 },
    {
      field: "actions",
      value: "Actions",
      sticky: "right",
      width: 50,
      renderCell: (experiment: Experiment) => (
        <Button
          variant="text"
          color="secondary"
          size="medium"
          onClick={(e) => handleMenuOpen(e, experiment)}
          dataDogIdentifier="experiment notes ellipsis action"
        >
          …
        </Button>
      )
    }
  ].map((obj) => {
    if (!isSmallTitle) return obj;
    // I just selected this number so that most of the single word columns would be full length.
    if (obj.value.length < 8) return obj;
    obj.value = obj.value
      .split(" ")
      .map((word) => word.charAt(0))
      .join("");
    return obj;
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(columns.map((column) => column.field));

  const handleDrop = (sourceIndex: number, targetIndex: number) => {
    const columnOrderNew = [...columnOrder];
    const [removed] = columnOrderNew.splice(sourceIndex, 1);
    columnOrderNew.splice(targetIndex, 0, removed);
    setColumnOrder(columnOrderNew);
  };

  return (
    <Page backLinkText={brandName} title="Experiments">
      <CommandBarWrapper>
        <FormControl sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
          <TextField
            size="small"
            placeholder="Search by Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ "& .MuiInputBase-root": { height: "32px" }, alignSelf: "flex-start" }}
            InputProps={{
              startAdornment: <Box sx={{ mr: 1 }}>🔍</Box>,
              endAdornment: searchQuery && (
                <Box
                  sx={{
                    cursor: "pointer",
                    ml: 1,
                    "&:hover": { opacity: 0.7 }
                  }}
                  onClick={() => setSearchQuery("")}
                >
                  ×
                </Box>
              )
            }}
            dataDogIdentifier="experiment search"
          />
          <Select
            labelId="product-filter-label"
            value={selectedProduct}
            onChange={handleProductChange}
            displayEmpty
            size="small"
            label="Filter by Product"
          >
            <MenuItem value="">All Products</MenuItem>
            {products.map((product) => (
              <MenuItem key={product.id} value={product.name}>
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={toggleSortDirection}
          startIcon={sortDirection === "desc" ? "↓" : sortDirection === "asc" ? "↑" : ""}
          dataDogIdentifier="experiment sort"
        >
          {sortDirection !== null
            ? `Sort by Hit Rate ${sortDirection === "desc" ? "(High to Low)" : "(Low to High)"}`
            : "Default Sort"}
        </Button>
        <Switch checked={isSmallTitle} onChange={() => setIsSmallTitle(!isSmallTitle)} size="small" />
      </CommandBarWrapper>
      {error ? (
        <div>Error: {error.message || "Failed to load experiments. Please try again later."}</div>
      ) : (
        <Table
          columns={
            columnOrder
              .map((field) => columns.find((column) => column.field === field))
              .filter(
                (column): column is NonNullable<typeof column> => column !== undefined
              ) as readonly TableColumn<Experiment>[]
          }
          rows={filteredExperiments}
          isLoading={isLoading || productsLoading}
          onDropColumn={handleDrop}
          tableId={`experiments-${brandId}`}
          stickyHeaders={true}
          height="79vh"
          dataDogIdentifier="experiments page"
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        <MenuItem size="medium" onClick={handleViewImages}>
          View Images
        </MenuItem>
        <MenuItem size="medium" onClick={handleViewDetails}>
          View Details
        </MenuItem>
        <MenuItem size="medium" onClick={handleMarkEvaled}>
          Mark Evaled
        </MenuItem>
        <MenuItem size="medium" onClick={handleRerunEval}>
          Rerun Eval
        </MenuItem>
        <MenuItem size="medium" onClick={handleRetryExperiment}>
          Retry Experiment
        </MenuItem>
        <MenuItem size="medium" onClick={handleRetrainExperiment}>
          Retrain Experiment (with the same params)
        </MenuItem>
      </Menu>
    </Page>
  );
};

export default ExperimentsPage;
